import { useMemo } from 'react'
import { useGuildMembers } from 'app/frontend/base/config/queries'
import { DataTable, EmptyMessageWithLoader } from '@/components/ui/data-table'
import { ColumnDef } from '@tanstack/react-table'
import {
  characterClass,
  name,
  realm,
  level,
  itemLevel,
  refreshedAt,
  detailedRankInGuild,
} from 'app/frontend/base/config/column-definitions/character'
import { useTranslation } from 'react-i18next'
import { updateGuild } from 'app/frontend/base/api/guild'
import { Button } from '@/components/ui/button'

import Timestamp from 'app/frontend/base/components/Timestamp'
import { useGuild } from 'app/frontend/base/config/context/guild'
import Section from 'app/frontend/base/components/Section'
import useBasePath from 'app/frontend/base/hooks/useBasePath'
import { useCharacterDetailsStore } from 'app/frontend/base/state/useCharacterDetailsStore'
import { useLogo } from 'app/frontend/base/hooks/useLogo'
import { useRefreshData } from 'app/frontend/base/hooks/useRefreshData'

const GuildMembers = () => {
  const baseGuildPath = useBasePath('guild')
  const { guild, guildReference } = useGuild()
  const { t } = useTranslation()
  const { detailsStore, fetchNewDetails } =
    useCharacterDetailsStore<wowaudit.CharacterReference>(
      (character) => character.id,
    )

  const {
    data: members,
    isPending,
    isLoadingError,
    error,
    refetch,
  } = useGuildMembers(guildReference?.id)

  const { Logo } = useLogo()

  const { refreshing, refreshData } = useRefreshData({
    retriever: updateGuild,
    id: guildReference?.id,
    successKey: 'dashboard.guild.guild-update-success',
    callback: refetch,
    queryToInvalidate: ['guild', { path: baseGuildPath }],
  })

  const columns: ColumnDef<wowaudit.CharacterReference>[] = useMemo(
    () => [
      name,
      characterClass,
      realm,
      level,
      itemLevel(detailsStore, (character) => character.id),
      detailedRankInGuild(guild?.guildRanks),
      refreshedAt(t, 'guild'),
    ],
    [detailsStore, guild?.guildRanks, t],
  )

  return (
    <Section title={`Members | ${guild?.name}`}>
      <h2 className='flex justify-between'>
        <span>
          Guild members {guild?.guildMembers ? `(${guild.guildMembers})` : ''}
        </span>

        <div className='text-sm flex flex-col gap-2 items-end font-normal'>
          <Button
            variant='outline'
            size='sm'
            loading={refreshing}
            spinner='blizzard'
            className='flex items-center gap-3 min-w-38'
            disabled={!guildReference?.id}
            onClick={() => refreshData()}
          >
            <Logo className='w-6 h-6' />
            {t('shared.update-now')}
          </Button>

          <div className='flex gap-1 text-xs text-right'>
            <span>{t('shared.last-updated')}</span>
            <Timestamp
              time={guild?.characterDataRefreshedAt}
              colorNever={false}
            />
          </div>
        </div>
      </h2>

      <DataTable
        columns={columns}
        data={members || []}
        includeHeader={true}
        pagination={50}
        onVisibleRowsChange={fetchNewDetails}
        filterType='includesString'
        sortingState={[{ id: 'rankInGuild', desc: false }]}
        emptyMessage={() => (
          <EmptyMessageWithLoader
            emptyMessageKey='dashboard.guild.no-members'
            isPending={isPending}
            isLoadingError={isLoadingError}
            error={error}
            t={t}
          />
        )}
      />
    </Section>
  )
}

export default GuildMembers
